import {
	NavigationCommand,
	NavigationInstruction,
	Redirect,
	RedirectToRoute,
	Router,
} from 'aurelia-router';

import { Logger } from '~/utils/logger';

export class RouteState {
	/**
	 * This loads a new route without discarding the old route. If the original parameters will cause an error
	 * when loading the old route, that error could still occur before this new route is activated.
	 */
	public static navigateToSameURLWithNewRouteParameters = (
		router: Router | undefined,
		params: { [paramName: string]: any },
	): void => {
		if (!router) {
			throw new Error(
				`Unable to update route parameters. Router isn't initialized yet!`,
			);
		}
		const originalParams = {
			...router.currentInstruction.params,
			...router.currentInstruction.queryParams,
		};
		const newComputedParams = RouteState.computeParams({
			...originalParams,
			...params,
		});
		if (
			!RouteState.hasURLParamsChanged(originalParams, newComputedParams)
		) {
			// NOTE: Jake: 2019-05-13 - WEBUI-1967
			// This if-statement was introduced so that when you visit a navigation area such as "Ledgers by Member - Alter View"
			// you won't get a double redirect to the same page.
			// This is kind-of a quick fix as ideally "app.ts" and "entity-search.ts" should not be re-routing the
			// user to different locations, it should be handled before activate via an Aurelia pipeline.
			Logger.log('No changes to params. Not updating route.');
			return;
		}
		Logger.log(
			'Updating route parameters for',
			router.currentInstruction.config.name,
			'| Params:',
			newComputedParams,
		);
		const path = router.generate(
			router.currentInstruction.config.name,
			newComputedParams,
		);
		const pathAndParams = path.split('?');
		if (pathAndParams.length > 1) {
			// NOTE(Jae): 2020-02-10 - WEBUI-2986
			// Convert %20 (space) after ? to +.
			// This is because Aurelias router.generate functions don't do this for us.
			// Things still seem to work, at least in Chrome without this change, however
			// this makes the "client" value in the URL look aesthetically better to me and Umit.
			// Source: https://stackoverflow.com/a/29948396/5013410
			pathAndParams[1] = pathAndParams[1].replace(/%20/g, '+');
			router.navigate(pathAndParams[0] + '?' + pathAndParams[1]);
			return;
		}
		router.navigate(pathAndParams[0]);
	};

	public static getRedirectForRouteParameters(
		navigationInstruction: NavigationInstruction,
		params: { [paramName: string]: any },
	): NavigationCommand {
		const computedParams = RouteState.computeParams({
			...navigationInstruction.params,
			...navigationInstruction.queryParams,
			...params,
		});

		// Verify that the current URL matches what we expect it to. If not, it's possible we received
		// a request to navigate to a different form from the Acurity Thick Client while we were processing.
		const currentAnchor = window.location.hash;
		const currentAnchorSplit = currentAnchor.split('?');
		const currentFragment = currentAnchorSplit[0];
		// Use substring to extract the entire query string as a single string.
		const currentQueryString =
			currentAnchorSplit.length > 1
				? currentAnchor.substring(currentAnchor.indexOf('?') + 1)
				: '';
		if (
			currentFragment !== '#' + navigationInstruction.fragment ||
			currentQueryString !== navigationInstruction.queryString
		) {
			Logger.log(
				'Navigation instruction fragment=' +
					navigationInstruction.fragment +
					' queryString=' +
					navigationInstruction.queryString +
					' does not match current anchor ' +
					currentAnchor +
					"', so aborting redirect.",
			);
			return new Redirect(currentAnchor, {
				replace: true,
				trigger: true,
			});
		}
		Logger.log(
			'Updating route parameters for',
			navigationInstruction.config.name,
			'| Params:',
			computedParams,
		);
		return new RedirectToRoute(
			navigationInstruction.config.name,
			computedParams,
			{ replace: true, trigger: true },
		);
	}

	/**
	 * hasURLParamsChanged checks to see if:
	 * - any parameters have been removed (to support clearing member entity - WEBUI-1989)
	 * - any parameters have had their values changed
	 */
	private static hasURLParamsChanged(
		params: { [paramName: string]: any },
		newParams: { [paramName: string]: any },
	): boolean {
		const hasChanged =
			Object.keys(params).length !== Object.keys(newParams).length ||
			Object.keys(newParams).some(
				(key) => params[key] !== newParams[key],
			);
		return hasChanged;
	}

	/**
	 * computeParams will concat the first two objects together and then iterate over the "new parameters" and either:
	 * - Remove that param from the final result if its falsey (ie. null, undefined or '')
	 * - Add that param if its truthy.
	 *
	 * @throws {Error}
	 */
	private static computeParams(params: { [paramName: string]: any }): {
		[paramName: string]: any;
	} {
		if (Object.keys(params).length === 0) {
			throw new Error('Cannot pass 0 new URL parameters to "params"');
		}
		for (const paramName in params) {
			if (!params.hasOwnProperty(paramName)) {
				continue;
			}
			const param = params[paramName];
			if (param) {
				params[paramName] = param;
			} else {
				// Allow removal of params by explicitly passing
				// falsey values.
				delete params[paramName];
			}
		}
		return params;
	}
}
